import { Platform, type ViewStyle } from 'react-native';

/** Above fixed web menu (1000) and in-dialog overlay layers */
export const MODAL_PORTAL_Z_INDEX = 10001;
export const MODAL_OVERLAY_Z_INDEX = 10001;
export const MODAL_CONTENT_Z_INDEX = 10002;
/** Nested pickers (date range, selects) opened from inside StyledModal / Dialog */
export const NESTED_PICKER_Z_INDEX = MODAL_CONTENT_Z_INDEX + 100;
/**
 * CompanySelector.web MUI Menu uses ~200050. Add-company / auth dialogs must sit above it
 * or success/error feedback appears "under" the open selector.
 */
export const COMPANY_SELECTOR_MENU_Z_INDEX = 200050;
export const ADD_COMPANY_MODAL_WEB_Z_INDEX = COMPANY_SELECTOR_MENU_Z_INDEX + 50;
/** Tamagui Sheet/Dialog on native must sit above app chrome */
export const NATIVE_SHEET_Z_INDEX = 200000;
/** Auth/error toasts must sit above native sheets (e.g. mail login) */
export const AUTH_MODAL_NATIVE_Z_INDEX = NATIVE_SHEET_Z_INDEX + 10000;
/** Web auth must sit above company selector menu (200050) and add-company dialog */
export const AUTH_MODAL_WEB_Z_INDEX = ADD_COMPANY_MODAL_WEB_Z_INDEX + 50;

export type AdaptiveModalTheme = {
  modalSurface: string;
  panelSurface: string;
  panelBorder: string;
  titleText: string;
  bodyText: string;
  subtleText: string;
  mutedSurface: string;
  mutedBorder: string;
  mutedText: string;
  primary: string;
  primaryDark: string;
  onPrimary: string;
  iconBadgeSurface: string;
  iconBadgeColor: string;
};

export const getAdaptiveModalTheme = (isLightTheme: boolean): AdaptiveModalTheme => {
  if (isLightTheme) {
    return {
      modalSurface: 'rgba(255, 255, 255, 0.98)',
      panelSurface: '#f8fafc',
      panelBorder: '#e5e7eb',
      titleText: '#1e293b',
      bodyText: '#374151',
      subtleText: '#64748b',
      mutedSurface: '#f1f5f9',
      mutedBorder: '#e2e8f0',
      mutedText: '#64748b',
      primary: '#3b82f6',
      primaryDark: '#2563eb',
      onPrimary: '#ffffff',
      iconBadgeSurface: '#f0f9ff',
      iconBadgeColor: '#0ea5e9',
    };
  }

  return {
    modalSurface: 'rgba(30, 41, 59, 0.98)',
    panelSurface: '#374151',
    panelBorder: '#4b5563',
    titleText: '#f1f5f9',
    bodyText: '#f9fafb',
    subtleText: '#94a3b8',
    mutedSurface: '#4b5563',
    mutedBorder: '#6b7280',
    mutedText: '#d1d5db',
    primary: '#2563eb',
    primaryDark: '#1d4ed8',
    onPrimary: '#ffffff',
    iconBadgeSurface: '#0c4a6e',
    iconBadgeColor: '#38bdf8',
  };
};

/** Canonical settings-dialog chrome (CustomDialog `settings` variant). */
export const getSettingsModalShellStyle = (
  modalTheme: AdaptiveModalTheme,
  options?: { zIndex?: number; minWidth?: number | string; maxWidth?: number | string; width?: number | string }
): ViewStyle => ({
  zIndex: options?.zIndex,
  padding: 0,
  minWidth: options?.minWidth,
  maxWidth: options?.maxWidth,
  width: options?.width,
  maxHeight: '85%',
  borderRadius: 24,
  overflow: 'hidden',
  borderWidth: 1,
  borderColor: modalTheme.panelBorder,
  backgroundColor: modalTheme.modalSurface,
  elevation: 20,
  shadowColor: '#000',
  shadowOffset: { width: 0, height: 4 },
  shadowOpacity: 0.15,
  shadowRadius: 16,
  ...(Platform.OS === 'web' && {
    backdropFilter: 'blur(24px)',
    boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
  }),
});

export const getSettingsModalHeaderStyle = (modalTheme: AdaptiveModalTheme): ViewStyle => ({
  backgroundColor: modalTheme.panelSurface,
  paddingHorizontal: 24,
  paddingVertical: 20,
  borderBottomWidth: 1,
  borderBottomColor: modalTheme.panelBorder,
  flexDirection: 'row',
  alignItems: 'center',
  gap: 12,
});

export const getSettingsModalFooterStyle = (modalTheme: AdaptiveModalTheme): ViewStyle => ({
  backgroundColor: modalTheme.panelSurface,
  paddingHorizontal: 24,
  paddingVertical: 16,
  borderTopWidth: 1,
  borderTopColor: modalTheme.panelBorder,
  flexDirection: 'row',
  gap: 12,
  justifyContent: 'flex-end',
});
